// ── Corporate Directives ────────────────────────────────────────────────────

const DIRECTIVE_POOL = [
  { type: 'revenue_target', descTemplate: 'Generate {cash_target} in revenue this quarter', targetFn: (stage) => [5000, 12000, 20000][stage - 2] || 10000, penalty: 'fine' },
  { type: 'quality_harvest', descTemplate: 'Harvest with quality score 70+ at least {target} times', targetFn: () => 2, penalty: 'rep' },
  { type: 'fulfill_contracts', descTemplate: 'Fulfill {target} contracts this quarter', targetFn: (stage) => stage >= 3 ? 3 : 2, penalty: 'rep' },
  { type: 'maintain_reputation', descTemplate: 'Maintain reputation above {target} for 4 consecutive weeks', targetFn: (stage) => [30, 40, 55][stage - 2] || 35, penalty: 'rep' },
  { type: 'no_blackmarket', descTemplate: 'No black market sales this quarter', targetFn: () => 0, penalty: 'fine' },
  { type: 'hire_scientist', descTemplate: 'Have at least 1 scientist employed', targetFn: () => 1, penalty: 'rep' },
  { type: 'start_research', descTemplate: 'Begin a research project', targetFn: () => 1, penalty: 'rep' },
];

export function generateDirective(state) {
  // Filter pool by stage availability
  const available = DIRECTIVE_POOL.filter(d => {
    if (d.type === 'hire_scientist' && state.stage > 2) return false; // only stage 2
    return true;
  });
  // Don't repeat the same type as last directive
  const lastType = state.corporateDirective?.type;
  const filtered = available.filter(d => d.type !== lastType);
  const pool = filtered.length > 0 ? filtered : available;

  const template = pool[Math.floor(Math.random() * pool.length)];
  const target = template.targetFn(state.stage);
  const description = template.descTemplate
    .replace('{cash_target}', `$${target.toLocaleString()}`)
    .replace('{target}', target);

  return {
    id: `directive-${Date.now()}`,
    type: template.type,
    description,
    target,
    progress: 0,
    startDay: state.gameDay,
    deadline: state.gameDay + 91, // ~13 weeks
    penalty: template.penalty,
    quarterNum: Math.floor(state.gameWeek / 13) + 1,
  };
}

export function checkDirectiveProgress(state, directive) {
  if (!directive) return 0;
  switch (directive.type) {
    case 'hire_scientist':
      return state.workers.filter(w => w.type === 'scientist').length >= directive.target ? directive.target : 0;
    case 'start_research':
      return state.activeResearch ? 1 : 0;
    case 'maintain_reputation':
      // This is tracked weekly — progress = consecutive weeks above target
      // Each week in processWeek, if rep >= target, progress++; else reset to 0
      return directive.progress; // don't override here, updated in processWeek
    case 'no_blackmarket':
      return directive.progress; // 0 = good, -1 = failed (set on blackmarket sale)
    default:
      return directive.progress; // revenue_target, quality_harvest, fulfill_contracts tracked by events
  }
}

export function evaluateDirectiveCompletion(state) {
  const directive = state.corporateDirective;
  if (!directive) return null;

  let success = false;
  switch (directive.type) {
    case 'no_blackmarket':
      success = directive.progress >= 0; // -1 means they sold blackmarket
      break;
    case 'maintain_reputation':
      success = directive.progress >= 4; // need 4 consecutive weeks above target
      break;
    default:
      success = directive.progress >= directive.target;
  }

  return { success, directive };
}

// ── Board Pressure ──────────────────────────────────────────────────────────

export function getSatisfactionLevel(satisfaction) {
  if (satisfaction >= 60) return 'satisfied';
  if (satisfaction >= 40) return 'concerned';
  if (satisfaction >= 20) return 'hostile';
  return 'takeover';
}

// ── Buyback ─────────────────────────────────────────────────────────────────

export function calculateBuybackPrice(state) {
  const basePrice = 1500000;
  const revenueValuation = state.totalRevenue * 4 * 0.25;
  const stageDiscount = state.stage >= 4 ? 0.75 : 1.0;
  return Math.round(Math.max(basePrice, revenueValuation) * stageDiscount);
}
